/**
 * Order Service - Manages orders in localStorage
 * Orders persist even after page refresh
 */

const STORAGE_KEY = 'mughal_ainak_orders';

/**
 * Get all orders from localStorage
 * @returns {Array} Array of all orders
 */
export const getAllOrders = () => {
  try {
    const ordersJson = localStorage.getItem(STORAGE_KEY);
    if (!ordersJson) {
      return [];
    }
    return JSON.parse(ordersJson);
  } catch (error) {
    console.error('Error reading orders from localStorage:', error);
    return [];
  }
};

/**
 * Save an order to localStorage
 * @param {Object} orderData - Complete order data
 * @returns {Object} Saved order with order number and timestamp
 */
export const saveOrder = (orderData) => {
  try {
    const orders = getAllOrders();
    
    // Create a unique identifier for this order based on customer phone, total, and items
    const createOrderHash = (data) => {
      const itemsKey = data.items?.map(item => `${item.id}-${item.quantity}`).join(',') || '';
      return `${data.customer?.phone || ''}-${data.total || 0}-${itemsKey}`;
    };
    
    const newOrderHash = createOrderHash(orderData);
    
    // Check if an identical order exists (within last 10 seconds to handle quick duplicates)
    const now = Date.now();
    const existingOrder = orders.find(order => {
      const orderHash = createOrderHash(order);
      const orderTime = new Date(order.timestamp).getTime();
      const timeDiff = now - orderTime;
      return orderHash === newOrderHash && timeDiff < 10000; // Within 10 seconds
    });
    
    if (existingOrder) {
      console.log('Order already exists, skipping duplicate save');
      return existingOrder;
    }
    
    const orderNumber = `ORD-${Date.now()}`;
    const timestamp = new Date().toISOString();
    
    const order = {
      orderNumber,
      timestamp,
      ...orderData
    };

    orders.unshift(order); // Add to beginning of array (newest first)
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
    
    return order;
  } catch (error) {
    console.error('Error saving order to localStorage:', error);
    throw error;
  }
};

/**
 * Remove duplicate orders (keeps the first occurrence)
 * @returns {number} Number of duplicates removed
 */
export const removeDuplicateOrders = () => {
  try {
    const orders = getAllOrders();
    const seen = new Set();
    const unique = [];
    let duplicatesRemoved = 0;
    
    orders.forEach(order => {
      // Create unique key from order data
      const itemsKey = order.items?.map(item => `${item.id}-${item.quantity}`).join(',') || '';
      const orderKey = `${order.customer?.phone || ''}-${order.total || 0}-${itemsKey}-${order.timestamp?.substring(0, 16)}`;
      
      if (!seen.has(orderKey)) {
        seen.add(orderKey);
        unique.push(order);
      } else {
        duplicatesRemoved++;
      }
    });
    
    if (duplicatesRemoved > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
      console.log(`Removed ${duplicatesRemoved} duplicate order(s)`);
    }
    
    return duplicatesRemoved;
  } catch (error) {
    console.error('Error removing duplicate orders:', error);
    return 0;
  }
};

/**
 * Get a specific order by order number
 * @param {string} orderNumber - Order number to find
 * @returns {Object|null} Order object or null if not found
 */
export const getOrderByNumber = (orderNumber) => {
  try {
    const orders = getAllOrders();
    return orders.find(order => order.orderNumber === orderNumber) || null;
  } catch (error) {
    console.error('Error getting order:', error);
    return null;
  }
};

/**
 * Get orders by customer phone number
 * @param {string} phone - Customer phone number
 * @returns {Array} Array of matching orders
 */
export const getOrdersByPhone = (phone) => {
  try {
    const orders = getAllOrders();
    return orders.filter(order => 
      order.customer?.phone?.replace(/\D/g, '') === phone.replace(/\D/g, '')
    );
  } catch (error) {
    console.error('Error getting orders by phone:', error);
    return [];
  }
};

/**
 * Delete an order by order number
 * @param {string} orderNumber - Order number to delete
 * @returns {boolean} True if deleted, false otherwise
 */
export const deleteOrder = (orderNumber) => {
  try {
    const orders = getAllOrders();
    const filtered = orders.filter(order => order.orderNumber !== orderNumber);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting order:', error);
    return false;
  }
};

/**
 * Clear all orders (useful for testing)
 */
export const clearAllOrders = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing orders:', error);
    return false;
  }
};

const orderService = {
  getAllOrders,
  saveOrder,
  getOrderByNumber,
  getOrdersByPhone,
  deleteOrder,
  clearAllOrders,
  removeDuplicateOrders
};

export default orderService;

